import { BulbTwoTone } from '@ant-design/icons';
import GGButton from 'components/buttons/GGButton';
import AppColors from 'config/AppColors';
import dayjs, { Dayjs } from 'dayjs';
import useSymptomTrackingData, { CycleStats } from 'hooks/useSymptomTrackingData';
import { locale } from 'locales/i18nManager';
import posthog from 'posthog-js';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useNavigate } from 'react-router-dom';
import { routes } from 'services/RouteService';
import LinearCalendarChart from './LinearCalendarChart';
import PeriodChart from './PeriodChart';
import SymptomsChart from './SymptomsChart';
import SymptomsChartLegend from './SymptomsChartLegend';

const useStyles = createUseStyles({
  graphSelector: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: '12px 0px',
    '& .ant-radio-button-wrapper': {
      border: 'none',
    },
  },
  graphsContainer: {
    overflowX: 'auto',
    overflowY: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
    paddingTop: 18,
    position: 'relative',
  },
  graphsContainerNoScroll: {
    composes: '$graphsContainer',
    overflowX: 'hidden',
  },
  pleaseFillYourDataOverlay: {
    color: AppColors.text.veryLightGray,
    fontSize: 14,
    textAlign: 'center',
    padding: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,

    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',

    background: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(2px)',

    '& p': {
      margin: 0,
    },
  },
  pleaseFillYourDataOverlayText: {
    background: `radial-gradient(closest-side, rgba(255,255,255,1), rgba(255,255,255,0))`,
    textAlign: 'center',
    padding: '48px 36px',
    borderRadius: 8,
    color: AppColors.colorTextBase,
  },
  selectedGraph: {
    color: AppColors.main,
  },
  unselectedGraphLink: {
    color: AppColors.text.discrete,
  },
});

const CompareCycles: React.FC<{ onFillPastSymptomsClicked: () => void }> = ({ onFillPastSymptomsClicked }) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { cycleDaysWithCenteredPeriod, getStatusValueForDisplayedDays, hasDataOtherThanPeriod, dataOtherThanPeriod } =
    useSymptomTrackingData();

  const noData = !hasDataOtherThanPeriod || cycleDaysWithCenteredPeriod.length === 0;
  const notEnoughData =
    !noData &&
    dayjs(dataOtherThanPeriod.firstDayWithData?.date).isSameOrAfter(
      cycleDaysWithCenteredPeriod[cycleDaysWithCenteredPeriod.length - 1].days[0],
    );

  const graphsContainerRef = useRef<HTMLDivElement>(null);

  /*
  const maxLeftOffset = cycleDaysWithCenteredPeriod.reduce((max, { days, cycle }) => {
    return Math.min(max, days[0].diff(cycle.start, 'days'));
  }, 0);
  const maxRightOffset = cycleDaysWithCenteredPeriod.reduce((max, { days, cycle }) => {
    return Math.max(max, days[days.length - 1].diff(cycle.start, 'days'));
  }, 0);
  */
  const maxLeftOffset = noData ? -14 : -24;
  const maxRightOffset = noData ? 14 : 25;

  // scroll to the middle of the container
  useEffect(() => {
    if (graphsContainerRef.current) {
      if (!noData) {
        const { scrollWidth, clientWidth } = graphsContainerRef.current;
        graphsContainerRef.current.scrollTo({
          left: scrollWidth / 2 - clientWidth / 2,
        });
      } else {
        graphsContainerRef.current.scrollTo({
          left: 0,
        });
      }
    }
  }, [graphsContainerRef, noData, notEnoughData]);

  const [selectedGraph, setSelectedGraph] = useState<'generalState' | 'period' | 'pain' | 'symptoms'>('generalState');
  const graphs: ('generalState' | 'period' | 'pain' | 'symptoms')[] = ['generalState', 'period', 'pain', 'symptoms'];

  const getGraphLabel = (graph: 'generalState' | 'period' | 'pain' | 'symptoms'): string => {
    switch (graph) {
      case 'generalState':
        return t('symptomTracking.compare.generalState');
      case 'period':
        return t('symptomTracking.compare.period');
      case 'pain':
        return t('symptomTracking.compare.pain');
      case 'symptoms':
        return t('symptomTracking.compare.symptoms');
      default:
        return '';
    }
  };

  const renderGraph = (days: Dayjs[], cycle: CycleStats) => {
    const dayRange: [Dayjs, Dayjs] = [cycle.start.add(maxLeftOffset, 'days'), cycle.start.add(maxRightOffset, 'days')];

    const displayedDays = cycle.predicted ? days.filter(day => day.isSameOrBefore(dayjs(), 'day')) : days;

    const topLabels: [Dayjs, string][] = [
      [cycle.start, cycle.start.toDate().toLocaleDateString(locale, { month: 'long', day: 'numeric' })],
    ];

    const key = days[0].date();
    // eslint-disable-next-line default-case
    switch (selectedGraph) {
      case 'generalState':
        return (
          <LinearCalendarChart
            key={key}
            dayLabels={displayedDays}
            dayValues={getStatusValueForDisplayedDays('general_state', displayedDays)}
            range={[0, 4]}
            dayRange={dayRange}
            periodDots={Array.from({ length: cycle.periodLength }, (_, i) => cycle.start.add(i, 'days'))}
            drawHollowPeriodDots={cycle.predicted}
            topLabels={topLabels}
          />
        );
      case 'period':
        return (
          <PeriodChart
            key={key}
            dayLabels={displayedDays}
            dayValues={getStatusValueForDisplayedDays('menstruation_amount', displayedDays)}
            dayRange={dayRange}
            periodDots={
              cycle.predicted ? Array.from({ length: cycle.periodLength }, (_, i) => cycle.start.add(i, 'days')) : []
            } // empty list to maintain a consistent height, but do not display any dots for readability
            drawHollowPeriodDots={cycle.predicted}
            topLabels={topLabels}
          />
        );
      case 'pain':
        return (
          <LinearCalendarChart
            key={key}
            dayLabels={displayedDays}
            dayValues={getStatusValueForDisplayedDays('pain_level', days)}
            range={[0, 4]}
            reverseColors
            dayRange={dayRange}
            periodDots={Array.from({ length: cycle.periodLength }, (_, i) => cycle.start.add(i, 'days'))}
            drawHollowPeriodDots={cycle.predicted}
            topLabels={topLabels}
          />
        );
      case 'symptoms':
        return (
          <SymptomsChart
            key={key}
            dayLabels={displayedDays}
            dayValues={getStatusValueForDisplayedDays('symptoms', displayedDays, [])}
            dayRange={dayRange}
            periodDots={Array.from({ length: cycle.periodLength }, (_, i) => cycle.start.add(i, 'days'))}
            drawHollowPeriodDots={cycle.predicted}
            topLabels={topLabels}
          />
        );
      default:
        return null;
    }
  };

  const helperStubCycleData: { days: Dayjs[]; cycle: CycleStats }[] = [
    {
      days: Array.from({ length: 28 }, (_, i) => dayjs().add(i, 'days')),
      cycle: {
        start: dayjs().add(14, 'days'),
        periodLength: 5,
        totalLength: 28,
        completed: true,
      },
    },
  ];

  return (
    <div>
      <div className={styles.graphSelector}>
        {graphs.map(graph => (
          <a
            key={graph}
            onClick={() => {
              setSelectedGraph(graph);
              posthog.capture('tracking.compare_graph_selected', { graph });
            }}
            className={selectedGraph === graph ? styles.selectedGraph : styles.unselectedGraphLink}
          >
            {getGraphLabel(graph)}
          </a>
        ))}
      </div>
      <div className={noData ? styles.graphsContainerNoScroll : styles.graphsContainer} ref={graphsContainerRef}>
        {noData
          ? helperStubCycleData.map(({ days, cycle }) => {
              return renderGraph(days, cycle);
            })
          : cycleDaysWithCenteredPeriod
              .reverse()
              .slice(0, notEnoughData ? 1 : 4)
              .map(({ days, cycle }) => {
                return renderGraph(days, cycle);
              })}
        {noData && (
          <div className={styles.pleaseFillYourDataOverlay}>
            <div className={styles.pleaseFillYourDataOverlayText}>
              <p style={{ fontSize: 24 }}>
                <BulbTwoTone style={{ color: '#FFF5D6' }} twoToneColor="#FFD147" />
              </p>
              <p>{t('symptomTracking.compare.fillToCompare')}</p>
            </div>
          </div>
        )}
      </div>
      {selectedGraph === 'symptoms' && (
        <SymptomsChartLegend
          dayValues={getStatusValueForDisplayedDays(
            'symptoms',
            cycleDaysWithCenteredPeriod.flatMap(({ days }) => days),
            [],
          )}
        />
      )}
      {noData && (
        <GGButton
          block
          size="large"
          type="primary"
          onClick={() => {
            navigate(routes.symptomTrackingSurvey.route);
            posthog.capture('tracking.survey.started', {
              source: 'compare_cycles',
            });
          }}
        >
          {t('symptomTracking.actions.fillDailySurvey')}
        </GGButton>
      )}
      {notEnoughData && (
        <>
          <div className={styles.graphsContainerNoScroll}>
            {helperStubCycleData.map(({ days, cycle }) => {
              return renderGraph(days, cycle);
            })}
            <div className={styles.pleaseFillYourDataOverlay}>
              <div className={styles.pleaseFillYourDataOverlayText}>
                <p style={{ fontSize: 24 }}>
                  <BulbTwoTone style={{ color: '#FFF5D6' }} twoToneColor="#FFD147" />
                </p>
                <p>{t('symptomTracking.compare.fillMultipleCycles')}</p>
              </div>
            </div>
          </div>
          <GGButton block size="large" type="link" onClick={onFillPastSymptomsClicked}>
            {t('symptomTracking.history.fillPastSymptoms')}
          </GGButton>
        </>
      )}
    </div>
  );
};

export default CompareCycles;
